export const FLOATING_MAP_PANEL_INSET = 18;
export const FLOATING_MAP_INITIAL_POSITION = {
  x: FLOATING_MAP_PANEL_INSET,
  y: FLOATING_MAP_PANEL_INSET + 6,
};

export const FLOATING_MAP_SIZE_PRESETS = {
  compact: { width: 440, mapHeight: 236 },
  standard: { width: 520, mapHeight: 500 },
  large: { width: 640, mapHeight: 620 },
  xlarge: { width: 760, mapHeight: 720 },
};

export const FLOATING_MAP_SIZE_OPTIONS = [
  { key: "compact", label: "Compact" },
  { key: "standard", label: "Standard" },
  { key: "large", label: "Grand" },
  { key: "xlarge", label: "Très grand" },
];

export const FLOATING_MAP_MIN_WIDTH = 420;
export const FLOATING_MAP_MAX_WIDTH = 1240;
export const FLOATING_MAP_MIN_HEIGHT = 220;
export const FLOATING_MAP_MAX_HEIGHT = 980;

const FLOATING_MAP_EXTRA_VIEWPORT_ALLOWANCE_X = 960;
const FLOATING_MAP_EXTRA_VIEWPORT_ALLOWANCE_Y = 420;

function resolveNumericPosition(value, fallbackValue) {
  return Number.isFinite(Number(value)) ? Number(value) : fallbackValue;
}

function resolveViewportExtent(axis = "width") {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return axis === "height" ? FLOATING_MAP_MAX_HEIGHT : FLOATING_MAP_MAX_WIDTH;
  }

  const documentElement = document.documentElement;
  const viewportWidth = Math.max(documentElement?.clientWidth || 0, window.innerWidth || 0);
  const viewportHeight = Math.max(documentElement?.clientHeight || 0, window.innerHeight || 0);
  return axis === "height" ? viewportHeight : viewportWidth;
}

function resolveFloatingMapMaxWidth() {
  const viewportWidth = resolveViewportExtent("width");
  return Math.max(FLOATING_MAP_MAX_WIDTH, viewportWidth - (FLOATING_MAP_PANEL_INSET * 2));
}

function resolveFloatingMapMaxHeight() {
  const viewportHeight = resolveViewportExtent("height");
  return Math.max(FLOATING_MAP_MAX_HEIGHT, viewportHeight - (FLOATING_MAP_PANEL_INSET * 2));
}

export function resolveFloatingMapPreset(size = "standard", sizePresets = FLOATING_MAP_SIZE_PRESETS) {
  const resolvedSizePresets = sizePresets && typeof sizePresets === "object"
    ? sizePresets
    : FLOATING_MAP_SIZE_PRESETS;

  return resolvedSizePresets[size] || resolvedSizePresets.standard || FLOATING_MAP_SIZE_PRESETS.standard;
}

export function clampFloatingMapWidth(width) {
  const resolvedWidth = resolveNumericPosition(width, resolveFloatingMapPreset().width);
  return Math.min(Math.max(FLOATING_MAP_MIN_WIDTH, resolvedWidth), resolveFloatingMapMaxWidth());
}

export function clampFloatingMapHeight(height) {
  const resolvedHeight = resolveNumericPosition(height, resolveFloatingMapPreset().mapHeight);
  return Math.min(Math.max(FLOATING_MAP_MIN_HEIGHT, resolvedHeight), resolveFloatingMapMaxHeight());
}

export function resolveFloatingMapDimensions(size = "standard", customWidth = null, customMapHeight = null, sizePresets = FLOATING_MAP_SIZE_PRESETS) {
  const preset = resolveFloatingMapPreset(size, sizePresets);
  const width = customWidth == null ? preset.width : clampFloatingMapWidth(customWidth);
  const mapHeight = customMapHeight == null ? preset.mapHeight : clampFloatingMapHeight(customMapHeight);

  return {
    mapHeight,
    width,
  };
}

export function clampFloatingMapDocumentPosition(position = FLOATING_MAP_INITIAL_POSITION, panelElement) {
  const nextX = resolveNumericPosition(position?.x, FLOATING_MAP_INITIAL_POSITION.x);
  const nextY = resolveNumericPosition(position?.y, FLOATING_MAP_INITIAL_POSITION.y);

  if (!(panelElement instanceof HTMLElement) || typeof window === "undefined") {
    return {
      x: Math.max(FLOATING_MAP_PANEL_INSET, nextX),
      y: Math.max(FLOATING_MAP_PANEL_INSET, nextY),
    };
  }

  const documentElement = document.documentElement;
  const viewportWidth = Math.max(documentElement?.clientWidth || 0, window.innerWidth || 0);
  const viewportHeight = Math.max(documentElement?.clientHeight || 0, window.innerHeight || 0);
  const maxX = Math.max(
    FLOATING_MAP_PANEL_INSET,
    window.scrollX + viewportWidth + FLOATING_MAP_EXTRA_VIEWPORT_ALLOWANCE_X - panelElement.offsetWidth - FLOATING_MAP_PANEL_INSET,
  );
  const maxY = Math.max(
    FLOATING_MAP_PANEL_INSET,
    window.scrollY + viewportHeight + FLOATING_MAP_EXTRA_VIEWPORT_ALLOWANCE_Y - panelElement.offsetHeight - FLOATING_MAP_PANEL_INSET,
  );

  return {
    x: Math.min(Math.max(FLOATING_MAP_PANEL_INSET, nextX), maxX),
    y: Math.min(Math.max(FLOATING_MAP_PANEL_INSET, nextY), maxY),
  };
}

export function resolveAbsoluteFloatingMapAnchorPosition(anchorElement) {
  if (!(anchorElement instanceof HTMLElement) || typeof window === "undefined") {
    return null;
  }

  const anchorRect = anchorElement.getBoundingClientRect();
  return {
    x: anchorRect.left + window.scrollX,
    y: anchorRect.top + window.scrollY,
  };
}

export function resolveDefaultFloatingMapDocumentPosition(panelElement, anchorPosition = null) {
  if (!(panelElement instanceof HTMLElement) || typeof window === "undefined") {
    return { ...FLOATING_MAP_INITIAL_POSITION };
  }

  if (Number.isFinite(Number(anchorPosition?.x)) && Number.isFinite(Number(anchorPosition?.y))) {
    return clampFloatingMapDocumentPosition(anchorPosition, panelElement);
  }

  const documentElement = document.documentElement;
  const viewportWidth = Math.max(documentElement?.clientWidth || 0, window.innerWidth || 0);

  return clampFloatingMapDocumentPosition(
    {
      x: window.scrollX + viewportWidth - panelElement.offsetWidth - FLOATING_MAP_PANEL_INSET,
      y: window.scrollY + FLOATING_MAP_INITIAL_POSITION.y,
    },
    panelElement,
  );
}